import type { ILinkProps } from "@components/cms/link/Link";
import type { TextProps } from "@ecco/ui/text";
import type { InputProps } from "@ecco/ui/input";
import type { BoxProps } from "@ecco/ui/box";
import type { ContainerProps } from "@ecco/ui/container";
import type { FlexProps } from "@ecco/ui/flex";
import type { GridProps } from "@ecco/ui/grid";
import type { NextLinkProps } from "@components/ui/next-link/NextLink";
import { scrollbarStyles } from "@ecco/ui/css/scrollbar";
import type { CSSProperties } from "react";
import { sizes, space } from "@ecco/ui/tokens";
import type { StyleProps } from "@ecco/ui/types";

export const navContainerStyles: ContainerProps = {
    justifyContent: "center",
    maxW: "100%",
    boxSizing: "border-box",
    border: "none",
    px: { base: 1, lg: 4 },
};

export const containerStyles: BoxProps = {
    display: "flex",
    justifyContent: "space-between",
    w: "100%",
    maxW: "container.largeDesktop",
    mx: "auto",
    h: [10.4, "64px"],
};

export const logoStyles: FlexProps = {
    ml: 2,
    mr: 6,
    flexShrink: 0,
};

export const mobileLogoContainerStyles: FlexProps = {
    zIndex: 1,
    justifyContent: "space-between",
    w: "100%",
    h: sizes.navBarCollapsedHeight,
    alignItems: "center",
};

export const innerNavLeftGridStyles: GridProps = {
    columnGap: 5,
    rowGap: 6,
    h: "fit-content",
    w: "100%",
    bg: "white",
};

export const outerNavLeftStyles: BoxProps["sx"] = {
    width: "100%",
    height: "auto",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    gap: 0,
    overflowX: "hidden",
    "&:hover": {
        ...scrollbarStyles,
        "::-webkit-scrollbar-track": {
            bg: "gray.200",
        },
    },
    "::-webkit-scrollbar": {
        width: sizes.customScrollbarWidth,
        height: sizes.customScrollbarWidth,
    },
    "::-webkit-scrollbar-thumb": { background: "transparent" },
    "::-webkit-scrollbar-track": { background: "transparent" },
};

export const innerNavRightStyles: FlexProps["sx"] = {
    position: "sticky",
    maxHeight: sizes.full,
    width: "100%",
    overflow: "auto",
    flexDirection: "column",
    maxW: "100%",
    gap: 10,
    overflowX: "hidden",
    "&:hover": {
        "::-webkit-scrollbar-thumb": { background: "black" },
        "::-webkit-scrollbar-track": { background: "gray.200" },
    },
    "::-webkit-scrollbar": {
        width: sizes.customScrollbarWidth,
        height: sizes.customScrollbarWidth,
    },
    "::-webkit-scrollbar-thumb": { background: "transparent" },
    "::-webkit-scrollbar-track": { background: "transparent" },
    paddingRight: "15px",
};

export const menuContainerStyles: ContainerProps = {
    display: "flex",
    justifyContent: "space-between",
    maxW: "container.largeDesktop",
    mx: "auto",
    gap: 3,
    height: `calc(${sizes.navExpandedHeight} - var(--nav-dropdown-top, ${sizes.navBarCollapsedHeight}))`,
    paddingLeft: "30px",
    paddingRight: "15px",
    paddingTop: "30px",
    paddingBottom: "30px",
};

export const navItemContainerStyles: FlexProps["sx"] = {
    px: 1,
    background: "linear-gradient(black 0 0) 12px 99% /var(--d, 0) 1.5px no-repeat",
    transition: "cubic-bezier(0.85, 0, 0.15, 1)0.5s",
    bg: "white",
    height: "28px",
    lineHeight: "28px",
    display: "flex",
    alignItems: "center",
    mr: -0.2,
};

/** Transparent hit area for mega-menu hover; stretches to nav height without affecting link pill layout. */
export const navItemHoverWrapperStyles: FlexProps["sx"] = {
    alignSelf: "stretch",
    display: "flex",
    alignItems: "center",
    bg: "transparent",
};

export const smallHeadingStyles: TextProps = {
    fontWeight: "bold",
    color: "black",
    textTransform: "uppercase",
    fontSize: "desktopBodyTextNormal",
    marginBottom: 4,
};

export const navItemStyles: NextLinkProps["sx"] = {
    fontSize: "md",
    whiteSpace: "nowrap",
    fontWeight: "light",
    px: 2,
    _focus: {
        outline: "none",
    },
    _focusVisible: {
        outline: "1px solid black",
        outlineOffset: "0px",
    },
    _active: {
        fontWeight: "bold",
    },
};

export const linkItemStyles: ILinkProps["sx"] = {
    fontSize: "desktopBodyTextNormal",
    fontWeight: "normal",
    display: "inline-block",
    whiteSpace: "wrap",
    lineHeight: "shorter",
    maxW: "225px",
    _focusVisible: {
        outlineOffset: "-1px",
        outline: "1px solid black",
    },
    _active: {
        fontWeight: "bold",
    },
};

export const titleLinkStyles: ILinkProps["sx"] = {
    ...linkItemStyles,
    fontWeight: "bold",
    textTransform: "uppercase",
    marginBottom: 4,
};

export const animatedContainerStyles: BoxProps = {
    position: "fixed",
    top: `var(--nav-dropdown-top, ${sizes.navBarCollapsedHeight})`,
    left: 0,
    right: 0,
    width: "100%",
    background: "white",
    overflow: "visible",
    zIndex: 10,
};

/** Shared hover open/close delay for desktop mega menu (ms). */
export const NAV_HOVER_DELAY_MS = 500;

export const animatedContainerMobileStyles: CSSProperties = {
    whiteSpace: "nowrap",
    right: "0",
    left: "0",
    height: "auto",
    width: "100vw",
    position: "fixed",
    top: "53px",
    maxHeight: "calc(100dvh - 52px)",
    flexDirection: "column",
    justifyContent: "space-between",
    overflow: "auto",
    zIndex: 44,
    gap: 32,
};

// Normal weight — bold was making all mobile nav items heavy
export const categoryTitleMobileStyles: TextProps["sx"] = {
    fontWeight: "normal",
    color: "black",
    fontSize: "desktopHeading5",
};

export const mobileMenuContainerStyles: FlexProps["sx"] = {
    display: ["flex", "flex", "flex", "flex", "flex", "none"],
    pt: space["2"],
    flexDirection: "column",
    position: "relative",
    inset: "auto 0",
};

export const navItemMobileStyles: FlexProps["sx"] = {
    justifyContent: "space-between",
    alignItems: "center",
    w: "100%",
    alignContent: "center",
    bg: "transparent",
    pb: space["4"],
    px: 4,
    _active: { bg: "transparent" },
    _hover: { bg: "transparent" },
};

export const linksContainerStyles = {
    justifyContent: "flex-start",
    backdropFilter: "blur(7.5px)",
    gap: space["0.2"],
    backgroundColor: "transparent",
    position: "relative",
    display: { base: "none", md: "none", lg: "flex" },
    w: "auto",
    minW: 0,
    overflow: "hidden",
} satisfies StyleProps;

export const bottomContainerLinkStyles: NextLinkProps["sx"] = {
    display: "flex",
    fontSize: "desktopBodyTextNormal",
    fontWeight: "light",
    textDecoration: "underline",
    gap: sizes[2],
    alignItems: "center",
    _focusVisible: {
        outlineOffset: "-1px",
        outline: "1px solid black",
    },
};

export const bottomContainerMobileStyles: FlexProps["sx"] = {
    px: 4,
    flexDirection: "column",
    bg: "white",
    py: 4,
    gap: 4,
};

export const mobileButtonBackStyles: FlexProps["sx"] = {
    w: "100%",
    bg: "white",
    justifyContent: "flex-start",
    alignContent: "center",
    fontWeight: "normal",
    color: "black",
    gap: 2,
    fontSize: "desktopHeading5",
};

export const seeAllCategoriesMobileStyles: FlexProps["sx"] = {
    fontWeight: "normal",
    color: "black",
    fontSize: "desktopHeading5",
    pb: space["3"],
    pt: 5,
    px: 4,
    alignItems: "center",
    justifyContent: "space-between",
};

export const parentThumbnailContainerStyles: BoxProps = {
    position: "relative",
    height: "auto",
    my: 6,
};

export const thumbnailCarouselContainerStyles: FlexProps["sx"] = {
    display: "flex",
    maxWidth: "100vw",
    flexWrap: "nowrap",
    overflowY: "auto",
    pb: 2,
    ...scrollbarStyles,
    left: 0,
    px: 4,
    "::-webkit-scrollbar-track": {
        background: "gray.200",
        marginRight: 4,
        marginLeft: 4,
    },
};

export const thumbnailCarouselItemStyles: FlexProps = {
    height: "100%",
    gap: 2,
    flexWrap: "nowrap",
    width: "auto",
};

export const mobileLinkStyles: ILinkProps["sx"] = {
    ...categoryTitleMobileStyles,
    textTransform: "none",
    py: space["3"],
    px: 4,
    _hover: { borderColor: "transparent", outline: "none" },
    _focus: { outlineColor: "transparent", outline: "none", border: "none" },
    _focusVisible: { outlineColor: "transparent", outline: "none", border: "none" },
    _active: { outlineColor: "transparent", outline: "none", border: "none" },
};

export const searchInputStyles: InputProps = {
    color: "black",
    borderRadius: "none",
    transform: "scale(1)",
    marginRight: 0,
    px: 10,
    py: 3,
    height: [sizes.navBarCollapsedHeight],
    zIndex: 2,
    borderColor: {
        base: "gray.500",
        lg: "transparent",
    },
    fontSize: "desktopBodyTextSmall",
    _placeholder: {
        color: "gray.200",
        fontSize: "desktopBodyTextSmall",
        whiteSpace: "nowrap",
        textOverflow: "ellipsis",
    },
    _hover: { outline: "none" },
    _focus: { outlineColor: "initial", outline: "none" },
    _focusVisible: { outlineColor: "initial", outline: "none" },
    _active: { outlineColor: "initial", outline: "none" },
};

export const toggleNavigationButtonStyles: FlexProps["sx"] = {
    backdropFilter: "blur(7.5px)",
};

// ─── Desktop sales styles ─────────────────────────────────────────────────────

export const salesNavItemContainerStyles: FlexProps["sx"] = {
    ...navItemContainerStyles,
    bg: "#A42B1E",
};

export const salesNavItemStyles: NextLinkProps["sx"] = {
    ...navItemStyles,
    color: "white !important",
};

// ─── Mobile sales styles ──────────────────────────────────────────────────────
// Row is transparent — red bg only on the label text, white text on top

export const salesNavItemMobileStyles: FlexProps["sx"] = {
    ...navItemMobileStyles,
};

// Red bg pill on the label, white text — mirrors desktop item container
export const salesCategoryTitleMobileStyles: TextProps["sx"] = {
    ...categoryTitleMobileStyles,
    color: "white",
    bg: "#A42B1E",
    px: 2,
    py: 0.5,
    display: "inline-block",
};

export const salesMobileButtonBackStyles: FlexProps["sx"] = {
    ...mobileButtonBackStyles,
    color: "black",
};
